/**
 * F17.2.5 — Calcolo costi controsoffitto al m².
 *
 * Differenze chiave rispetto a `computeStratigraphyCosts` (parete):
 *  - Le pareti hanno UN solo spacing (`studSpacingMm`); i controsoffitti ne hanno DUE
 *    (`primarySpacingMm` per orditura primaria, `secondarySpacingMm` per la secondaria
 *    quando l'orditura è doppia).
 *  - I PENDINI sono un materiale accessorio extra-layer: l'utente li sceglie da un
 *    picker dedicato e il costo per m² è `pendini_per_m² × prezzo_pendino`.
 *  - L'isolante può stare SOPRA l'orditura primaria oppure NEL VANO secondario:
 *    in entrambi i casi si consuma 1 m² di pannello per m² (uno per vano).
 *  - Il PROFILO PERIMETRALE non è incluso qui (è ml × perimetro, calcolato al
 *    momento "Aggiungi a preventivo" perché dipende dall'area+perimetro della stanza).
 *
 * Formule:
 *  - board / ceiling_tile / finish: 1 m²/m²
 *  - structure_frame/guide (primario): ml/m² = 1000 / primarySpacingMm
 *  - structure_frame/guide (secondario): ml/m² = 1000 / secondarySpacingMm
 *  - insulation: 1 m²/m² in entrambi i casi (un pannello per vano)
 *  - pendini (selectedHanger): N pendini/m² = 1 / (primary * perpendicular / 1e6)
 *  - viti: già in layer.screwCostPerSqm
 *  - manodopera: somma `installation_time_per_sqm * quantity * 60` minuti
 */

import type { LayerV2 } from '../types';
import type { DatabaseMaterial } from '@/hooks/useMaterials';
import type { CeilingDimensionalState } from './useCeilingConfiguratorState';
import { estimateHangerCountPerSqm } from '../visualization/section-view/ceilingModel';

export interface CeilingCostRowMaterial {
  layerIdx: number;
  layerNum: number;
  name: string;
  category: string;
  /** Quantità per m² di controsoffitto (m²/mq, ml/ml, pz/pz). */
  quantity: number;
  unit: string;
  unitPriceNet: number;
  unitPriceList: number;
  rowCost: number;
  rowSavings: number;
  rowMinutes: number;
  /** Posizione semantica del profilo (utile per UI). */
  frameLevel?: 'primary' | 'secondary';
  /** Posizione semantica dell'isolante. */
  insulationPosition?: 'above_primary' | 'in_secondary_gap';
  notes?: string;
}

export interface CeilingCostRowScrews {
  layerIdx: number;
  layerNum: number;
  screwName: string;
  screwQuantityPerSqm: number;
  pricePerPiece: number;
  rowCost: number;
  rowMinutes: number;
}

/** Riga extra-layer dedicata ai pendini di sospensione. */
export interface CeilingCostRowHangers {
  hangerName: string;
  /** Numero pendini per m² (es. 1.23 per spacing 900×900). */
  countPerSqm: number;
  pricePerPiece: number;
  rowCost: number;
  rowMinutes: number;
}

export interface CeilingCostBreakdown {
  rows: CeilingCostRowMaterial[];
  screwRows: CeilingCostRowScrews[];
  /** Riga pendini — null se attachment_type='fixed' o nessun pendino selezionato. */
  hangerRow: CeilingCostRowHangers | null;
  subtotalMaterials: number;
  subtotalScrews: number;
  /** Costo pendini per m² (è già in hangerRow.rowCost, qui replicato per comodità). */
  subtotalHangers: number;
  laborMinutes: number;
  laborTime: number;
  laborCost: number;
  /** Totale €/m² (materiali + viti + pendini + manodopera). NON include perimetro. */
  totalCost: number;
  totalSavings: number;
}

/**
 * Calcola il breakdown costi del controsoffitto.
 *
 * @param layers      Layer della stratigrafia (lastre + profili + isolanti)
 * @param ceiling     Stato dimensionale (spacing primario/secondario, drop, attachment_type)
 * @param costPerHour Costo orario manodopera (€/h)
 * @param selectedHanger Materiale pendino scelto dall'utente (null = non incluso nel calcolo)
 */
export function computeCeilingCosts(
  layers: LayerV2[],
  ceiling: CeilingDimensionalState,
  costPerHour: number,
  selectedHanger: DatabaseMaterial | null = null,
): CeilingCostBreakdown {
  const rows: CeilingCostRowMaterial[] = [];
  const screwRows: CeilingCostRowScrews[] = [];
  let subtotalMaterials = 0;
  let subtotalScrews = 0;
  let laborMinutes = 0;
  let totalSavings = 0;

  // Tracking semantico: il primo profilo trovato è il primario, il secondo il secondario.
  // L'isolante prima del 2° profilo è 'above_primary', dopo è 'in_secondary_gap'.
  let frameCount = 0;

  layers.forEach((l, idx) => {
    const m = l.material;
    if (!m || !l.materialId) return;

    const cat = m.category;
    // Prezzo della CONFEZIONE, non dell'unita`: i listini quotano il sacco.
    // Stessa regola di `computeStratigraphyCosts` — le due schermate devono
    // dare lo stesso numero per lo stesso materiale, e la card del layer e`
    // letteralmente lo stesso componente.
    const box = Number(m.box_pieces ?? 0);
    const perUnit = (p: number) => (box > 0 ? p / box : p);
    const netPrice = perUnit(Number(m.net_price ?? m.unit_price ?? 0));
    const listPrice = perUnit(Number(m.list_price ?? Number(m.net_price ?? m.unit_price ?? 0)));
    const unit = m.unit ?? '—';

    let quantity = 0;
    let notes: string | undefined;
    let frameLevel: 'primary' | 'secondary' | undefined;
    let insulationPosition: 'above_primary' | 'in_secondary_gap' | undefined;

    if (cat === 'board' || cat === 'ceiling_tile' || cat === 'finish') {
      // €/m² → 1 m²/m²
      quantity = 1;
    } else if (cat === 'structure_frame' || cat === 'structure_guide') {
      // Primo profilo → primario, secondo → secondario
      const isPrimary = frameCount === 0;
      frameLevel = isPrimary ? 'primary' : 'secondary';
      const spacing = isPrimary
        ? ceiling.primarySpacingMm
        : (ceiling.secondarySpacingMm ?? ceiling.primarySpacingMm);
      quantity = 1000 / Math.max(1, spacing);
      notes = `${quantity.toFixed(2)} ml/m² (orditura ${isPrimary ? 'primaria' : 'secondaria'} passo ${spacing}mm)`;
      frameCount++;
    } else if (cat === 'insulation') {
      // Coverage dipende dalla posizione:
      // - above_primary (default): copre 100% del m²
      // - in_secondary_gap: copre il vano fra profili secondari
      if (frameCount >= 2 && ceiling.secondarySpacingMm) {
        // Come sulle pareti: un pannello per vano, quindi 1 m²/m². Contare
        // l'area geometrica del vano sottostimava il consumo e faceva dipendere
        // il tempo di posa dalla larghezza del profilo.
        insulationPosition = 'in_secondary_gap';
        quantity = 1;
        notes = '1,00 m²/m² — un pannello per ogni vano secondario';
      } else {
        insulationPosition = 'above_primary';
        quantity = 1;
        notes = '100% (steso sopra orditura primaria)';
      }
    } else if (cat === 'screw') {
      // Viti come materiale-layer ignorate (gestite via layer.screwMaterial)
      return;
    } else {
      // accessory, other → costo diretto 1:1
      quantity = 1;
    }

    const rowCost = netPrice * quantity;
    const rowSavings = (listPrice - netPrice) * quantity;
    const timePerUnitH = Number(m.installation_time_per_sqm ?? 0);
    const rowMinutes = timePerUnitH * quantity * 60;

    rows.push({
      layerIdx: idx,
      layerNum: idx + 1,
      name: m.name ?? '—',
      category: cat,
      quantity,
      unit,
      unitPriceNet: netPrice,
      unitPriceList: listPrice,
      rowCost,
      rowSavings,
      rowMinutes,
      frameLevel,
      insulationPosition,
      notes,
    });
    subtotalMaterials += rowCost;
    if (rowSavings > 0.001) totalSavings += rowSavings;
    if (rowMinutes > 0) laborMinutes += rowMinutes;

    // === VITI del layer (uguale al wall) ===
    // Costo ricalcolato dal prezzo corrente della vite: `layer.screwCostPerSqm`
    // e' congelato alla composizione e non segue listini/sconti dell'org.
    // Stessa correzione fatta in computeStratigraphyCosts.
    const screwQty = Number(l.screwQuantity ?? 0);
    const screw = l.screwMaterial;
    const pricePerPiece = (() => {
      if (!screw) return 0;
      const unitPrice = Number(screw.net_price ?? screw.unit_price ?? 0);
      if (!(unitPrice > 0)) return 0;
      const screwUnit = String(screw.unit ?? '').toLowerCase().trim();
      const box = Number(screw.box_pieces ?? 0);
      return (screwUnit === 'scatola' && box > 0) ? unitPrice / box : unitPrice;
    })();
    const screwCostPerSqm = pricePerPiece > 0 && screwQty > 0
      ? pricePerPiece * screwQty
      : Number(l.screwCostPerSqm ?? 0);
    if (screwCostPerSqm > 0 && screw && screwQty > 0) {
      const screwTimePerPieceH = Number(screw.installation_time_per_sqm ?? 0);
      const screwMinutes = screwTimePerPieceH * screwQty * 60;
      screwRows.push({
        layerIdx: idx,
        layerNum: idx + 1,
        screwName: screw.name ?? 'Vite',
        screwQuantityPerSqm: screwQty,
        pricePerPiece,
        rowCost: screwCostPerSqm,
        rowMinutes: screwMinutes,
      });
      subtotalScrews += screwCostPerSqm;
      laborMinutes += screwMinutes;
    } else if (screwCostPerSqm > 0) {
      subtotalScrews += screwCostPerSqm;
    }
  });

  // === PENDINI di sospensione (extra-layer) ===
  let hangerRow: CeilingCostRowHangers | null = null;
  let subtotalHangers = 0;
  if (ceiling.attachmentType === 'suspended' && selectedHanger) {
    const countPerSqm = estimateHangerCountPerSqm(
      ceiling.hangerSpacingPrimaryMm,
      ceiling.hangerSpacingPerpendicularMm,
    );
    const pricePerPiece = Number(selectedHanger.net_price ?? selectedHanger.unit_price ?? 0);
    const rowCost = pricePerPiece * countPerSqm;
    const timePerPieceH = Number(selectedHanger.installation_time_per_sqm ?? 0);
    const rowMinutes = timePerPieceH * countPerSqm * 60;
    hangerRow = {
      hangerName: selectedHanger.name ?? 'Pendino',
      countPerSqm,
      pricePerPiece,
      rowCost,
      rowMinutes,
    };
    subtotalHangers = rowCost;
    laborMinutes += rowMinutes;
  }

  const laborTime = laborMinutes / 60;
  const laborCost = laborTime * (costPerHour > 0 ? costPerHour : 30);
  const totalCost = subtotalMaterials + subtotalScrews + subtotalHangers + laborCost;

  return {
    rows,
    screwRows,
    hangerRow,
    subtotalMaterials,
    subtotalScrews,
    subtotalHangers,
    laborMinutes,
    laborTime,
    laborCost,
    totalCost,
    totalSavings,
  };
}

// ============================================================================
// Costo perimetro (separato — chiamato dal "Aggiungi a preventivo" ceiling)
// ============================================================================

/**
 * Calcola il costo del profilo perimetrale per una stanza specifica.
 *
 * Il profilo perimetrale è proporzionale al PERIMETRO della stanza (ml), NON
 * all'area. Si calcola al momento "Aggiungi a preventivo" quando l'utente
 * inserisce area_sqm + perimeter_m (o pre-fill 4·√area).
 *
 * @param perimeterM       Perimetro stanza in metri lineari (override utente o 4·√area)
 * @param perimeterProfile Materiale profilo perimetrale (es. cornice_perimetrale_controsoffitto)
 * @returns Costo totale del profilo perimetrale per quella stanza (€)
 */
export function computePerimeterProfileCost(
  perimeterM: number,
  perimeterProfile: DatabaseMaterial | null,
): { cost: number; minutes: number; mlNeeded: number } {
  if (!perimeterProfile || perimeterM <= 0) {
    return { cost: 0, minutes: 0, mlNeeded: 0 };
  }
  const pricePerMl = Number(perimeterProfile.net_price ?? perimeterProfile.unit_price ?? 0);
  // Sfrido 5% (tagli + sormonti agli spigoli)
  const mlNeeded = perimeterM * 1.05;
  const cost = pricePerMl * mlNeeded;
  const timePerMlH = Number(perimeterProfile.installation_time_per_sqm ?? 0);
  const minutes = timePerMlH * mlNeeded * 60;
  return { cost, minutes, mlNeeded };
}

/**
 * Stima il perimetro (m) di una stanza assumendo forma quadrata: P = 4·√area.
 * Usato come pre-fill del campo `perimeter_m` nel dialog "Aggiungi a preventivo".
 * Accuratezza ~70% per stanze reali (sottostima L-shape, sovrastima molto allungate).
 */
export function estimatePerimeterFromArea(areaSqm: number): number {
  if (areaSqm <= 0) return 0;
  return Math.round(4 * Math.sqrt(areaSqm) * 100) / 100;
}
